import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import EditStateSelect from '@ui/edit-state-wrapper/edit-state-select';
import {
  selectCurrentSeries,
  selectIsLoadingCurrentSeries,
  selectIsLoadingSeriesList,
  selectSeriesList,
} from '@/store/slices/series/selectors';
import useLoadChannelSeries from '../use-load-channel-project-categories';
import {
  appendSeriesSync,
  clearCurrentSeries,
  closeTopDrawer,
  loadCurrentSeriesForProject,
} from '@/store/slices/series';
import { getActiveChannel } from '@/store/slices/user/selectors';
import CreateSeries from '@/connection/series/create-series';
import toastTrigger from '@ui/toast/toast-trigger';
import DeleteEventFromSeries from '@/connection/series/delete-event-from-series';
import AddEventToSeries from '@/connection/series/add-event-to-series';
import { useProjectUuidParam } from '@/utils/use-project-uuid-param';

type ProjectSeriesSelectProps = {
  defaultSeries?: TReactSelectOption[];
  disabled?: boolean;
};

const ProjectSeriesSelect = ({ defaultSeries, disabled = false }: ProjectSeriesSelectProps) => {
  useLoadChannelSeries({});
  const projectUuid = useProjectUuidParam();

  const loadingSeries = useAppSelector(selectIsLoadingSeriesList);
  const loadingCurrentSeries = useAppSelector(selectIsLoadingCurrentSeries);
  const channel = useAppSelector(getActiveChannel);
  const seriesList = useAppSelector(selectSeriesList);
  const currentSeries = useAppSelector(selectCurrentSeries);
  const dispatch = useAppDispatch();
  const [selectedSeries, setSelectedSeries] = useState<TReactSelectOption[] | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (currentSeries) {
      setSelectedSeries([
        {
          label: currentSeries.title,
          value: currentSeries.uuid,
          meta: currentSeries,
        },
      ]);
    } else {
      setSelectedSeries([]);
    }
  }, [currentSeries]);

  const options = useMemo(() => {
    return seriesList
      ?.map((series) => ({
        label: series.title,
        value: series.uuid,
        meta: series,
      }))
      .sort((a, b) => (a.label.toLocaleLowerCase() > b.label.toLocaleLowerCase() ? 1 : -1));
  }, [seriesList]);

  const handleMenuClose = useCallback(
    async (selectedOptions: TReactSelectOption[]) => {
      if (!projectUuid) {
        return;
      }

      const selected = selectedOptions[0];

      // handled in the onChange handler
      if (selected?.__isNew__) return;

      // find the original selected object
      const selectedSeries = seriesList?.find((ser) => ser.uuid === selected?.value);

      if (!selectedSeries && !currentSeries) {
        // nothing selected and nothing previously selected, do nothing.
        return;
      }

      try {
        setBusy(true);

        if (!selectedSeries && currentSeries?.uuid) {
          // delete from existing series, then do nothing
          dispatch(clearCurrentSeries());
          dispatch(closeTopDrawer());
          await DeleteEventFromSeries({
            channel: currentSeries.channel,
            seriesUuid: currentSeries.uuid,
            eventUuid: projectUuid,
          });
          return;
        }

        if (selectedSeries && !currentSeries) {
          // add to existing series
          await AddEventToSeries({
            channel: selectedSeries.channel,
            seriesUuid: selectedSeries.uuid,
            eventUuid: projectUuid,
          });

          dispatch(
            loadCurrentSeriesForProject({
              channel: selectedSeries.channel,
              eventUuid: projectUuid,
            })
          );
          return;
        }

        if (selectedSeries && currentSeries && selectedSeries.uuid !== currentSeries.uuid) {
          // delete from existing series, then add to new series
          await DeleteEventFromSeries({
            channel: currentSeries.channel,
            seriesUuid: currentSeries.uuid,
            eventUuid: projectUuid,
          });

          // add to existing series
          await AddEventToSeries({
            channel: selectedSeries.channel,
            seriesUuid: selectedSeries.uuid,
            eventUuid: projectUuid,
          });

          dispatch(
            loadCurrentSeriesForProject({
              channel: selectedSeries.channel,
              eventUuid: projectUuid,
            })
          );

          return;
        }
      } catch (e) {
        console.error(e);
      } finally {
        setBusy(false);
      }
    },
    [currentSeries, dispatch, projectUuid, seriesList]
  );

  const handleChange = useCallback(
    async (options: TReactSelectOption[]) => {
      const lastOption = options.at(-1);
      if (lastOption) {
        setSelectedSeries([lastOption]);
        if (lastOption.__isNew__ && channel && projectUuid) {
          try {
            setBusy(true);
            const newSeries = await CreateSeries({
              channel,
              title: String(lastOption.value),
              title_translation: { base: String(lastOption.value), changed: '' },
            });

            if (currentSeries) {
              await DeleteEventFromSeries({
                channel: currentSeries.channel,
                seriesUuid: currentSeries.uuid,
                eventUuid: projectUuid,
              });
            }

            // add to existing series
            await AddEventToSeries({
              channel: newSeries.channel,
              seriesUuid: newSeries.uuid,
              eventUuid: projectUuid,
            });

            dispatch(
              loadCurrentSeriesForProject({
                channel,
                eventUuid: projectUuid,
              })
            );

            dispatch(appendSeriesSync(newSeries));
            setSelectedSeries([{ label: newSeries.title, value: newSeries.uuid, meta: newSeries }]);
          } catch (e) {
            console.error(e);
            toastTrigger('Error creating new series', { type: 'error' });
          } finally {
            setBusy(false);
          }
        }
      } else {
        setSelectedSeries(undefined);
      }
    },
    [channel, currentSeries, dispatch, projectUuid]
  );

  return (
    <EditStateSelect
      isLoading={busy || loadingSeries || loadingCurrentSeries}
      Label="SERIES"
      placeholder="Select series"
      defaultValue={defaultSeries}
      value={selectedSeries}
      options={options}
      onChange={handleChange}
      onMenuClose={handleMenuClose}
      disabled={disabled || busy || loadingSeries || loadingCurrentSeries}
      isCreatable
      isMulti={false}
    />
  );
};

export default ProjectSeriesSelect;
